import { Stack, styled, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress, { type CircularProgressProps } from "@mui/material/CircularProgress";
import { atom, Provider as JotaiProvider, useAtom, useAtomValue } from "jotai";
import type React from "react";
import { useEffect, useState } from "react";
import SzCompactIcon from "../../../icons/react icons/sz-icons/SzCompactIcon.tsx";
import SzSimpleIcon from "../../../icons/react icons/sz-icons/SzSimpleIcon.tsx";

/**
 * SzLoading component props.
 * @typedef {Object} SzLoadingProps
 * @category ui
 * @subcategory feedback
 */
type SzLoadingProps = {
	/** Choose different color variant:
	 * "primary": Orange - Default if not specified.
	 * "secondary": Blue
	 * "mix": Changing color from orange to blue.
	 * "grey": Grey
	 *  */
	colorVariant?: "primary" | "secondary" | "mix" | "grey";
	/** Choose logo variant:
	 * "text": Logo of Sprava zeleznic with text "Sprava Zeleznic" at bottom.
	 * "textfree": Logo of sprava zeleznic without any name or text.
	 * If you doesn't put this option in, you will get empty circle loader.
	 *  */
	logoVariant?: "text" | "textfree";
	/** Choose different size variant in rem i.e.: sizeVariant={5}
	 * NOTE: You are typing in the number, but the units used are in rem.
	 * If you don't want to put this option in, default value would be used.
	 * Default is 4rem.
	 */
	sizeVariant?: number;
	/** Modification to use the icon as a label */
	content?: React.ReactNode;
	/** Translucent logo boolean
	 * If used logo will be translucent.
	 * NOTE: If you need translucent loading circle as well, you need to use both options.
	 */
	mildLogo?: boolean;
	/** Translucent circle boolean
	 * If used Loading circle will be translucent.
	 * NOTE: If you need translucent logo as well, you need to use both options.
	 */
	mildCircle?: boolean;
};

/**
 * Returns a new unique Symbol value.
 * @category ui
 * @subcategory feedback
 */

/**
 * Creates atom with initial values.
 * @category ui
 * @subcategory feedback
 */
const loadingAtom = atom<SzLoadingProps>({
	logoVariant: "text",
	sizeVariant: 3,
	colorVariant: "mix",
	mildCircle: false,
	mildLogo: false,
});

/**
 * Circle loader props.
 * @typedef {Object} SzCircleLoaderProps
 * @category ui
 * @subcategory feedback
 */
type SzCircleLoaderProps = {
	/** Choose different color variant:
	 * "primary": Orange - Default if not specified.
	 * "secondary": Bluetils
	 * "mix": Changing color from orange to blue.
	 * "grey": Grey
	 *  */
	colorVariant?: "primary" | "secondary" | "mix" | "grey";
	/** Translucent circle boolean
	 * If used Loading circle will be translucent.
	 * NOTE: If you need translucent logo as well, you need to use both options.
	 */
	mildCircle?: boolean;
} & CircularProgressProps;

/**
 * Styled Circular progress component.
 * Modifies color and or opacity of the Loading circle.
 * colorVariant determines color.
 * mildColor determines opacity.
 * @component
 * @category ui
 * @subcategory feedback
 */
const SzCircleLoader = styled(CircularProgress, {
	shouldForwardProp: (prop) => prop !== "colorVariant" && prop !== "mildCircle",
})<SzCircleLoaderProps>(({ theme }) => {
	const primaryOrange = (theme.vars || theme).palette.primary.main;
	const secondaryBlue = (theme.vars || theme).palette.primary.main;

	const loadingSets = useAtomValue(loadingAtom);
	const [colorState, setColorState] = useState(primaryOrange);

	setInterval(() => setColorState((prev) => (prev === primaryOrange ? secondaryBlue : primaryOrange)), 1500);

	return {
		zIndex: 1,
		...(loadingSets.mildCircle && {
			opacity: 0.6,
		}),
		...(loadingSets.colorVariant === "primary" && {
			color: primaryOrange,
		}),
		...(loadingSets.colorVariant === "secondary" && {
			color: secondaryBlue,
		}),
		...(loadingSets.colorVariant === "grey" && {
			color: (theme.vars || theme).palette.grey[400],
		}),
		...(loadingSets.colorVariant === "mix" && {
			color: colorState,
			transition: theme.transitions.create("color", { duration: 1500 }),
		}),
	};
});

/**
 * Styled Box element.
 * Center the Icon element inside the Circle Loader
 * @component
 * @category ui
 * @subcategory feedback
 */
const IconBox = styled(Box)(() => ({
	position: "absolute",
	alignItems: "center",
	justifyContent: "center",
	display: "flex",
	boxSizing: "border-box",
	top: "70%",
	left: "50%",
	transform: "translate(-50%, -50%)",
}));

/**
 * Styled Typography element.
 * Modifies size of the svg logo.
 * Determines if the logo will be translucent.
 * @component
 * @category ui
 * @subcategory feedback
 */
const Icon = styled(Typography)<SzLoadingProps>(() => {
	const loadingSets = useAtomValue(loadingAtom);

	const heightVariant = loadingSets.sizeVariant && loadingSets.sizeVariant * 0.75;

	return {
		fontSize: `${heightVariant?.toString()}rem`,
		...(loadingSets.mildLogo && {
			opacity: 0.6,
		}),
	};
});

/*
 * Two variants of svg logo for use as a label inside the loading circle.
 * text: Logo with text "Sprava zeleznic".
 * textfree: Simple logo without text.
 */
const iconTypes = {
	text: <SzCompactIcon fontSize="inherit" />,
	textfree: <SzSimpleIcon fontSize="inherit" />,
};
/**
 * Combines all the components together.
 * @param props
 * @component
 * @category ui
 * @subcategory feedback
 */
const Loading: React.FC<SzLoadingProps> = (props) => {
	const [loadingSets, setLoadingSets] = useAtom(loadingAtom);

	useEffect(() => {
		setLoadingSets({ ...props });
	}, [setLoadingSets]);

	return (
		<Stack justifyContent="center" alignItems="center" height="100%" width="100%">
			<Box sx={{ position: "relative", height: `${loadingSets.sizeVariant?.toString()}rem` }}>
				<SzCircleLoader
					colorVariant={loadingSets.colorVariant || "mix"}
					size={`${loadingSets.sizeVariant?.toString()}rem`}
					mildCircle={true}
				/>
				<IconBox>
					<Icon>{iconTypes[loadingSets.logoVariant as keyof typeof iconTypes]}</Icon>
				</IconBox>
			</Box>
		</Stack>
	);
};

/**
 * SZ Loader component
 * @param {SzLoadingProps} props - Props
 * @param {string} [props.colorVariant] - Choose different color variant:
 * @param {string} [props.logoVariant] - Logo variant
 * @param {number} [props.sizeVariant] - Size variant
 * @param {boolean} [props.mildLogo] - Translucent logo boolean
 * @param {boolean} [props.mildCircle] - Translucent circle boolean
 * @param {JSX.Element} [props.content] - Modification to use the icon as a label
 * @component
 * @category ui
 * @subcategory feedback
 */
const SzLoading: React.FC<SzLoadingProps> = (props) => (
	<JotaiProvider>
		<Loading data-testid="sz-loader" {...props} />
	</JotaiProvider>
);

export default SzLoading;
